import type { AufraeumBericht } from '../api';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Key, CheckCircle, AlertCircle, Loader2, RefreshCw, Cloud, AlertTriangle,
  Server as ServerIcon, Boxes, Users, Wand2, Download, Copy, Check, Layers, XCircle,
  Circle, HardDrive,
} from 'lucide-react';
import clsx from 'clsx';
import { servers, customers, baseStacks } from '../api';
import { useTabelle, TabellenKopf, TabellenLeiste, Blaetterleiste, type SpaltenDefinition, type FilterDefinition } from "../components/TabellenSteuerung";
import { FarbPunktSchalter, FarbWaehler } from "../components/Farbe";
import ListenReiter, { nachSicht, type ListenSicht } from "../components/ListenReiter";
import { useEinstellung } from '../contexts/EinstellungenContext';
import StandardsKnopf from '../components/StandardsKnopf';
import type {
  ProvisionServerPayload, HostingerCatalogItem, HetznerServerType, GeneratedKeyPair,
  BaseStack, PreflightResult,
} from '../api';
import StatusBadge from '../components/StatusBadge';
import DockerHostPanel from '../components/DockerHostPanel';
import BaseStackPanel from '../components/BaseStackPanel';
import HostCheckPanel from '../components/HostCheckPanel';
import StackAufnahmePanel from '../components/StackAufnahmePanel';
import SnapshotPanel from '../components/SnapshotPanel';
import RobotImportModal from '../components/RobotImportModal';
import ProxyZugangModal from '../components/ProxyZugangModal';
import MetrikPanel from '../components/MetrikPanel';
import AktionsMenue, { type Aktion } from '../components/AktionsMenue';
import type { Server, SshMeta } from '../types';
import ProxyWachePanel from '../components/ProxyWachePanel';

// ─── Types ────────────────────────────────────────────────────────────────────

// `SshMeta` und das `ssh`-Feld stehen jetzt am `Server`-Typ selbst (types.ts).
// Der Alias bleibt, damit die vielen Verwendungen hier unveraendert lesen.
type ServerWithSsh = Server;

type Ownership = 'SHARED' | 'DEDICATED_JUNI' | 'CUSTOMER_PROVIDED';

interface ServerForm {
  /** Hex oder leer — nur fuer die Uebersicht. */
  farbe: string;
  ownership: Ownership;
  customerId: string;
  providerAccountRef: string;
  hostname: string;
  ipAddress: string;
  provider: string;
  region: string;
  sshUser: string;
  sshPort: string;
  status: string;
  cpuCores: string;
  gpuModel: string;
  gpuVramMb: string;
  gpuCount: string;
  ramMb: string;
  diskGb: string;
  maxSlots: string;
  costPerMonth: string;
  notes: string;
  sshPrivateKey: string;
  sshPassphrase: string;
}

const DEFAULT_FORM: ServerForm = {
  farbe: '',
  ownership: 'SHARED',
  customerId: '',
  providerAccountRef: '',
  hostname: '',
  ipAddress: '',
  provider: 'manual',
  region: '',
  sshUser: 'root',
  sshPort: '22',
  status: 'READY',
  cpuCores: '',
  gpuModel: '',
  gpuVramMb: '',
  gpuCount: '',
  ramMb: '',
  diskGb: '',
  maxSlots: '6',
  costPerMonth: '',
  notes: '',
  sshPrivateKey: '',
  sshPassphrase: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Wieviele Instanzen auf dieser Maschine liegen — gegen die vorgesehene Zahl.
 *
 * `active` kommt aus `_count.instances` und nicht aus `activeSlots`: Letzteres
 * wird nirgends geschrieben und stand deshalb immer auf 0. Bei ancora hiess
 * das „0/1" bei zwei laufenden Instanzen — eine Anzeige, die schlechter ist als
 * keine, weil man ihr glaubt.
 *
 * Ueber der vorgesehenen Zahl wird der Balken voll und die Zahl amber, nicht
 * rot: es hindert niemanden, `maxSlots` wird an keiner Stelle geprueft. Es ist
 * eine Auskunft, keine Grenze.
 */
function SlotBar({ active, max }: { active: number; max: number }) {
  const pct = max > 0 ? Math.min((active / max) * 100, 100) : active > 0 ? 100 : 0;
  const drueber = max > 0 && active > max;
  const color = drueber
    ? 'bg-amber-500'
    : pct >= 90
      ? 'bg-red-500'
      : pct >= 70
        ? 'bg-amber-500'
        : 'bg-emerald-500';
  return (
    <div className="flex min-w-28 items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-slate-200">
        <div className={`${color} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span
        className={`whitespace-nowrap text-xs ${drueber ? 'text-amber-700' : 'text-slate-500'}`}
        title={
          drueber
            ? `${active} Instanzen bei ${max} vorgesehenen. Das hindert nichts — die Zahl ist eine Auskunft, keine Grenze.`
            : undefined
        }
      >
        {active}/{max}
      </span>
    </div>
  );
}

function SshKeyBadge({ meta }: { meta?: SshMeta }) {
  if (!meta?.hasKey) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Key size={12} /> Kein Key
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
      <CheckCircle size={12} />
      {meta.keyType ?? 'SSH Key'} {meta.version > 1 ? `v${meta.version}` : ''}
    </span>
  );
}

// ─── SSH Test Result Modal ────────────────────────────────────────────────────

interface SshTestState {
  serverId: string;
  hostname: string;
  status: 'idle' | 'testing' | 'ok' | 'error';
  output?: string;
  error?: string;
  durationMs?: number;
}

/**
 * `kompakt` zeigt nur das Symbol.
 *
 * Er bleibt als einziger Knopf neben dem Aktionsmenue stehen und wandert nicht
 * hinein: sein Ergebnis erscheint **unter** dem Knopf, und ein Menue, das sich
 * beim Klicken schliesst, haette es mitgenommen.
 */
function SshTestButton({ server, kompakt }: { server: ServerWithSsh; kompakt?: boolean }) {
  const [state, setState] = useState<SshTestState>({
    serverId: server.id,
    hostname: server.hostname,
    status: 'idle',
  });

  async function runTest() {
    setState((s) => ({ ...s, status: 'testing' }));
    try {
      const res = await servers.testSsh(server.id);
      setState((s) => ({
        ...s,
        status: res.success ? 'ok' : 'error',
        output: res.output,
        error: res.error,
        durationMs: res.durationMs,
      }));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Unbekannter Fehler';
      setState((s) => ({ ...s, status: 'error', error: msg }));
    }
  }

  return (
    <div className="space-y-1">
      <button
        onClick={runTest}
        disabled={state.status === 'testing' || !server.ssh?.hasKey}
        title={!server.ssh?.hasKey ? 'Zuerst SSH-Key hinterlegen' : 'SSH-Verbindung testen'}
        className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {state.status === 'testing' ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <RefreshCw size={12} />
        )}
        {!kompakt && 'SSH testen'}
      </button>
      {state.status === 'ok' && (
        <div className="text-xs text-emerald-600 flex items-center gap-1">
          <CheckCircle size={11} /> OK ({state.durationMs}ms)
        </div>
      )}
      {state.status === 'error' && (
        <div className="text-xs text-red-500 flex items-start gap-1 max-w-xs">
          <AlertCircle size={11} className="mt-0.5 shrink-0" />
          <span>{state.error}</span>
        </div>
      )}
    </div>
  );
}



/**
 * Replaces the old bare "Provider" column. On its own the provider says little —
 * what matters operationally is whether the machine is ours to fill up, reserved
 * for one customer, or one we merely have a login to.
 */
function OwnershipCell({ server }: { server: ServerWithSsh }) {
  const ownership = server.ownership ?? 'SHARED';
  const style =
    ownership === 'CUSTOMER_PROVIDED'
      ? 'bg-violet-50 text-violet-700 border-violet-200'
      : ownership === 'DEDICATED_JUNI'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-slate-50 text-slate-600 border-slate-200';
  const label =
    ownership === 'CUSTOMER_PROVIDED'
      ? 'Kundeneigen'
      : ownership === 'DEDICATED_JUNI'
        ? 'Dediziert'
        : 'Pool';

  return (
    <div className="space-y-0.5">
      <span className={clsx('inline-block rounded border px-1.5 py-0.5 text-xs font-medium', style)}>
        {label}
      </span>
      <div className="text-xs capitalize text-slate-500">{server.provider}</div>
      {server.customer && (
        <div className="max-w-40 truncate text-xs text-slate-600" title={server.customer.companyName}>
          {server.customer.companyName}
        </div>
      )}
    </div>
  );
}

// ─── Ownership selection ──────────────────────────────────────────────────────

const OWNERSHIP_OPTIONS: {
  value: Ownership;
  title: string;
  description: string;
  icon: typeof Users;
  defaults: Partial<ServerForm>;
}[] = [
  {
    value: 'SHARED',
    title: 'Gemeinsam genutzt',
    description: 'Unser Pool. Mehrere Kunden teilen sich die Maschine.',
    icon: Boxes,
    defaults: { maxSlots: '6', customerId: '' },
  },
  {
    value: 'DEDICATED_JUNI',
    title: 'Dediziert, von uns beschafft',
    description: 'Wir kaufen und betreiben sie, aber nur für einen Kunden.',
    icon: ServerIcon,
    // One tenant means one slot — a dedicated box that still advertises six
    // would get filled up by the scheduler, which is the opposite of dedicated.
    defaults: { maxSlots: '1' },
  },
  {
    value: 'CUSTOMER_PROVIDED',
    title: 'Kunde stellt selbst',
    description: 'Vertrag und Hardware gehören dem Kunden, wir haben nur Zugang.',
    icon: Users,
    // No contract of ours, so no cost of ours. And the access can be withdrawn
    // at any time — the same premise as a self-hosted instance.
    defaults: { maxSlots: '1', costPerMonth: '', provider: 'other' },
  },
];

function OwnershipSection({
  form,
  setForm,
}: {
  form: ServerForm;
  setForm: React.Dispatch<React.SetStateAction<ServerForm>>;
}) {
  const { t } = useTranslation();
  const { data: customerList = [] } = useQuery({
    queryKey: ['customers-for-server'],
    queryFn: () => customers.list(),
  });

  const needsCustomer = form.ownership !== 'SHARED';

  return (
    <div className="col-span-2 space-y-3">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          Wem gehört dieser Server?
        </label>
        <div className="grid grid-cols-3 gap-2">
          {OWNERSHIP_OPTIONS.map(({ value, title, description, icon: Icon, defaults }) => (
            <button
              key={value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, ownership: value, ...defaults }))}
              className={clsx(
                'rounded-lg border p-3 text-left transition-colors',
                form.ownership === value
                  ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                  : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50',
              )}
            >
              <Icon
                size={16}
                className={clsx(
                  'mb-1.5',
                  form.ownership === value ? 'text-brand-600' : 'text-slate-500',
                )}
              />
              <div className="text-sm font-medium text-slate-800">{title}</div>
              <div className="mt-0.5 text-xs leading-snug text-slate-500">{description}</div>
            </button>
          ))}
        </div>
      </div>

      {needsCustomer && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Kunde <span className="text-red-500">*</span>
            </label>
            <select
              value={form.customerId}
              onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">{t('forms.chooseCustomer')}</option>
              {customerList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Referenz beim Anbieter
              <span className="ml-1 font-normal text-slate-400">— optional</span>
            </label>
            <input
              value={form.providerAccountRef}
              onChange={(e) => setForm((f) => ({ ...f, providerAccountRef: e.target.value }))}
              placeholder={
                form.ownership === 'CUSTOMER_PROVIDED'
                  ? 'Vertrags-/Kundennummer des Kunden'
                  : 'unsere Bestellnummer'
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-slate-400">
              {form.ownership === 'CUSTOMER_PROVIDED'
                ? 'Damit der Support die Maschine im Konto des Kunden wiederfindet.'
                : 'Für die Zuordnung zur eigenen Beschaffung.'}
            </p>
          </div>
          {form.ownership === 'CUSTOMER_PROVIDED' && (
            <p className="col-span-2 flex items-start gap-1.5 text-xs text-amber-700">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              Der Zugang kann uns jederzeit entzogen werden. Für Produkte auf dieser Maschine
              sollte die Instanz im Fremd-Hosting-Modus laufen, damit Verwaltung und Aktivierung
              nicht am SSH-Zugang hängen.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SSH Key Section in modal ─────────────────────────────────────────────────

/** Triggers a browser download without a round trip to the server. */
function downloadText(filename: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Die Felder, die dieser Abschnitt wirklich anfasst — mehr braucht er nicht.
 *
 * Vorher stand hier `ServerForm`, und genau das war der Grund, warum es den
 * Schlüsselgenerator im Kaufdialog nicht gab: der arbeitet mit `ProvisionForm`,
 * einem anderen Typ. Die Komponente liess sich dort nicht einsetzen, also
 * standen im Kaufdialog drei nackte Textfelder — und wer keinen Schlüssel zur
 * Hand hatte, kaufte ohne.
 */
interface SshSchluesselFelder {
  hostname: string;
  sshPrivateKey: string;
  sshPassphrase: string;
}

function SshKeySection<T extends SshSchluesselFelder>({
  form,
  setForm,
  bezeichnung,
  onPublicKey,
}: {
  form: T;
  setForm: React.Dispatch<React.SetStateAction<T>>;
  /** Für Namen und Dateinamen, wenn die Maschine nicht `hostname` heisst — bei
   *  Hetzner und AWS trägt das Kaufformular den Namen in `name`. */
  bezeichnung?: string;
  /** Der Kaufdialog führt den öffentlichen Schlüssel als eigenes Feld, weil er
   *  ihn beim Anbieter hinterlegt. Wer ihn nicht braucht, lässt das weg. */
  onPublicKey?: (publicKey: string) => void;
}) {
  const { t } = useTranslation();
  const [showKey, setShowKey] = useState(false);
  const [generated, setGenerated] = useState<GeneratedKeyPair | null>(null);
  const [copied, setCopied] = useState(false);
  const [genError, setGenError] = useState('');

  const generate = useMutation({
    mutationFn: () =>
      servers.generateKeypair({
        comment: `juni-pc@${bezeichnung || form.hostname || 'server'}`,
        passphrase: form.sshPassphrase || undefined,
      }),
    onSuccess: (pair) => {
      setGenerated(pair);
      setGenError('');
      // Straight into the field the server is created from, so nobody has to
      // copy it across and risk truncating it.
      setForm((f) => ({ ...f, sshPrivateKey: pair.privateKey }));
      onPublicKey?.(pair.publicKey);
      setShowKey(true);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      setGenError(e.response?.data?.message ?? 'Schlüssel konnte nicht erzeugt werden');
    },
  });

  const baseName = (bezeichnung || form.hostname || 'server').replace(/[^\w.-]+/g, '-');

  return (
    <div className="col-span-2 border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <Key size={14} /> SSH-Zugangsdaten
        </h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-surface px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
          >
            {generate.isPending ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
            Schlüsselpaar erzeugen
          </button>
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            {showKey ? 'Ausblenden' : 'Einblenden'}
          </button>
        </div>
      </div>

      {genError && <p className="text-xs text-red-600">{genError}</p>}

      {generated && (
        <div className="space-y-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
          <div className="flex items-start gap-2">
            <CheckCircle size={15} className="mt-0.5 shrink-0 text-emerald-600" />
            <div className="text-xs text-emerald-900">
              <span className="font-medium">ED25519-Schlüsselpaar erzeugt.</span> Der private
              Teil steht unten im Feld und wird beim Speichern verschlüsselt abgelegt. Er wird
              danach nicht wieder angezeigt — jetzt herunterladen, wenn du ihn brauchst.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-emerald-900">
              Öffentlicher Schlüssel — auf dem Server in <code>~/.ssh/authorized_keys</code>
            </label>
            <div className="flex items-start gap-2">
              <code className="flex-1 break-all rounded border border-emerald-200 bg-surface px-2 py-1.5 font-mono text-[11px] leading-relaxed text-slate-800">
                {generated.publicKey}
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(generated.publicKey);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="shrink-0 rounded border border-emerald-300 bg-surface p-1.5 text-emerald-700 hover:bg-emerald-100"
                title="Kopieren"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
            </div>
            <p className="mt-1 font-mono text-[11px] text-emerald-800">
              Fingerprint: {generated.fingerprint}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => downloadText(`${baseName}_ed25519`, generated.privateKey)}
              className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-surface px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
            >
              <Download size={12} /> Privater Schlüssel
            </button>
            <button
              type="button"
              onClick={() =>
                downloadText(`${baseName}_ed25519.pub`, `${generated.publicKey}\n`)
              }
              className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-surface px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
            >
              <Download size={12} /> Öffentlicher Schlüssel
            </button>
          </div>
          <p className="text-[11px] text-emerald-800">
            Heruntergeladene Datei unter Linux/macOS auf <code>chmod 600</code> setzen, sonst
            verweigert OpenSSH die Nutzung.
          </p>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Privater SSH-Key (PEM-Format)
          <span className="ml-1 font-normal text-slate-400">{t('forms.optionalRecommended')}</span>
        </label>
        <textarea
          rows={showKey ? 8 : 3}
          value={form.sshPrivateKey}
          onChange={(e) => setForm((f) => ({ ...f, sshPrivateKey: e.target.value }))}
          placeholder={
            '-----BEGIN OPENSSH PRIVATE KEY-----\n' +
            'b3BlbnNzaC1rZXktdjEAAAAA…\n' +
            '-----END OPENSSH PRIVATE KEY-----'
          }
          spellCheck={false}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none bg-surface"
          style={{ lineHeight: '1.4' }}
        />
        <p className="text-xs text-slate-400 mt-1">
          Unterstützt: ED25519, RSA, ECDSA. Entweder oben erzeugen lassen, einen vorhandenen
          Schlüssel einfügen oder selbst erstellen mit{' '}
          <code className="bg-slate-200 px-1 rounded">ssh-keygen -t ed25519 -C "juni-pc"</code>
        </p>
      </div>

      {form.sshPrivateKey && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Passphrase <span className="font-normal text-slate-400">{t('forms.ifKeyProtected')}</span>
          </label>
          <input
            type="password"
            value={form.sshPassphrase}
            onChange={(e) => setForm((f) => ({ ...f, sshPassphrase: e.target.value }))}
            placeholder={t('forms.emptyIfNoPassword')}
            autoComplete="new-password"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      )}
    </div>
  );
}

// ─── Replace SSH Key modal ────────────────────────────────────────────────────

function ReplaceKeyModal({
  server,
  onClose,
}: {
  server: ServerWithSsh;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [key, setKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [erzeugt, setErzeugt] = useState<GeneratedKeyPair | null>(null);
  const [kopiert, setKopiert] = useState(false);

  /**
   * Schlüsselpaar erzeugen lassen, statt es von Hand anzulegen.
   *
   * Beim Anlegen eines Servers gibt es das längst; beim Tausch fehlte es — und
   * gerade dort ist es nützlich, denn ein Tausch passiert meist, weil der alte
   * Schlüssel kompromittiert oder verloren ist. Dann jemanden an die
   * Kommandozeile zu schicken, ist ein unnötiger Umweg.
   *
   * Der private Teil landet direkt im Formular. Der öffentliche muss auf den
   * Server — deshalb steht er hier zum Kopieren, mit dem Hinweis, dass ohne
   * diesen Schritt die Verbindung nach dem Speichern abreißt.
   */
  const erzeugen = useMutation({
    mutationFn: () =>
      servers.generateKeypair({
        comment: `juni-pc@${server.hostname}`,
        passphrase: passphrase || undefined,
      }),
    onSuccess: (paar) => {
      setErzeugt(paar);
      setKey(paar.privateKey);
      setError('');
    },
    onError: () => setError('Schlüssel konnte nicht erzeugt werden.'),
  });

  async function handleSave() {
    setError('');
    if (!key.trim().startsWith('-----BEGIN')) {
      setError('Key muss im PEM-Format sein (beginnt mit -----BEGIN …)');
      return;
    }
    setLoading(true);
    try {
      await servers.setSshKey(server.id, key, passphrase || undefined);
      qc.invalidateQueries({ queryKey: ['servers'] });
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Fehler beim Speichern.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-6 py-5 border-b border-slate-200">
          <h2 className="text-base font-semibold">SSH-Key ersetzen — {server.hostname}</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.privateSshKey')}</label>
            <textarea
              rows={8}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----'}
              spellCheck={false}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
            <div className="mt-1 flex items-center justify-between gap-2">
              <p className="text-xs text-slate-400">
                Empfohlen: ED25519 —{' '}
                <code className="bg-slate-100 px-1 rounded">ssh-keygen -t ed25519 -C "juni-pc"</code>
              </p>
              <button
                type="button"
                onClick={() => erzeugen.mutate()}
                disabled={erzeugen.isPending}
                className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-brand-400 hover:text-brand-700 disabled:opacity-50"
                title="Erzeugt ein ED25519-Paar. Der private Teil landet im Feld, der öffentliche muss auf den Server."
              >
                {erzeugen.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Wand2 size={12} />
                )}
                Schlüssel erzeugen
              </button>
            </div>
          </div>

          {erzeugt && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs font-semibold text-amber-900">
                Öffentlichen Schlüssel jetzt auf den Server bringen
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                Ohne diesen Schritt reißt die Verbindung ab, sobald der neue Schlüssel
                gespeichert ist — der PC käme dann nicht mehr auf {server.hostname}.
              </p>
              <pre className="mt-1.5 max-h-20 overflow-auto rounded bg-white/70 p-2 text-[11px] font-mono break-all whitespace-pre-wrap">
                {erzeugt.publicKey}
              </pre>
              <div className="mt-1 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(erzeugt.publicKey).then(() => {
                      setKopiert(true);
                      setTimeout(() => setKopiert(false), 2000);
                    });
                  }}
                  className="text-xs text-amber-900 underline hover:text-amber-950"
                >
                  {kopiert ? 'kopiert' : 'Öffentlichen Schlüssel kopieren'}
                </button>
                <span className="text-xs text-amber-700">
                  gehört in <code className="rounded bg-white/70 px-1">~/.ssh/authorized_keys</code>
                </span>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Passphrase <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder={t('forms.emptyIfNoPassword')}
              autoComplete="new-password"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600">
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !key.trim()}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60 flex items-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Key speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Auto-provision modal (Hetzner / Hostinger driver dispatch) ──────────────

interface ProvisionForm {
  provider: 'hetzner' | 'aws' | 'hostinger';
  name: string;
  serverType: string;
  image: string;
  location: string;
  itemId: string;
  templateId: string;
  dataCenterId: string;
  postInstallScriptId: string;
  instanceType: string;
  region: string;
  diskGb: string;
  hostname: string;
  sshPublicKey: string;
  sshPrivateKey: string;
  sshPassphrase: string;
  maxSlots: string;
  notes: string;
}

const DEFAULT_PROVISION_FORM: ProvisionForm = {
  provider: 'hetzner',
  name: '',
  serverType: 'cx22',
  image: 'ubuntu-24.04',
  location: '',
  itemId: '',
  templateId: '',
  dataCenterId: '',
  postInstallScriptId: '',
  instanceType: 't3.medium',
  region: '',
  diskGb: '80',
  hostname: '',
  sshPublicKey: '',
  sshPrivateKey: '',
  sshPassphrase: '',
  maxSlots: '6',
  notes: '',
};

function formatHostingerSpecs(metadata: HostingerCatalogItem['metadata']): string {
  const parts: string[] = [];
  if (metadata.cpus) parts.push(`${metadata.cpus} vCPU`);
  if (metadata.memory) parts.push(`${Math.round(parseInt(metadata.memory, 10) / 1024)} GB RAM`);
  if (metadata.disk_space) parts.push(`${Math.round(parseInt(metadata.disk_space, 10) / 1024)} GB Disk`);
  return parts.join(' / ');
}

function formatHostingerPriceLabel(
  item: HostingerCatalogItem,
  price: HostingerCatalogItem['prices'][number],
): string {
  const specs = formatHostingerSpecs(item.metadata);
  const months = price.period_unit === 'year' ? price.period * 12 : price.period;
  const perMonth = (price.price / 100 / months).toFixed(2);
  const total = (price.price / 100).toFixed(2);

  const periodLabel =
    price.period_unit === 'month' && price.period === 1
      ? 'monatlich, jederzeit kündbar'
      : `${price.period} ${price.period_unit === 'year' ? (price.period === 1 ? 'Jahr' : 'Jahre') : 'Monate'} im Voraus`;

  return `${item.name}${specs ? ` (${specs})` : ''} — ${perMonth} ${price.currency}/Monat · ${periodLabel}, gesamt ${total} ${price.currency}`;
}

function formatHetznerSpecs(t: HetznerServerType): string {
  return `${t.cores} vCPU (${t.cpu_type}) / ${t.memory} GB RAM / ${t.disk} GB Disk`;
}

function cheapestHetznerPrice(t: HetznerServerType): { location: string; monthly: number } | null {
  if (t.prices.length === 0) return null;
  return t.prices
    .map((p) => ({ location: p.location, monthly: parseFloat(p.price_monthly.gross) }))
    .sort((a, b) => a.monthly - b.monthly)[0];
}

function formatHetznerServerTypeLabel(t: HetznerServerType): string {
  const specs = formatHetznerSpecs(t);
  const cheapest = cheapestHetznerPrice(t);
  const priceLabel = cheapest ? `ab ${cheapest.monthly.toFixed(2)} €/Monat (${cheapest.location})` : 'kein Preis verfügbar';
  return `${t.name} — ${specs} — ${priceLabel}${t.deprecated ? ' [veraltet]' : ''}`;
}

function bytesToTb(bytes: number): string {
  return (bytes / 1024 ** 4).toFixed(bytes % 1024 ** 4 === 0 ? 0 : 1);
}

function ProvisionModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<ProvisionForm>(DEFAULT_PROVISION_FORM);
  const [error, setError] = useState('');

  const hetznerServerTypesQuery = useQuery({
    queryKey: ['hetzner', 'server-types'],
    queryFn: servers.hetznerServerTypes,
    enabled: form.provider === 'hetzner',
    retry: false,
  });
  const hetznerLocationsQuery = useQuery({
    queryKey: ['hetzner', 'locations'],
    queryFn: servers.hetznerLocations,
    enabled: form.provider === 'hetzner',
    retry: false,
  });
  const awsOptionsQuery = useQuery({
    queryKey: ['aws', 'options', form.region],
    queryFn: () => servers.awsOptions(form.region || undefined),
    enabled: form.provider === 'aws',
  });

  const hostingerCatalogQuery = useQuery({
    queryKey: ['hostinger', 'catalog-items'],
    queryFn: () => servers.hostingerCatalogItems('VPS'),
    enabled: form.provider === 'hostinger',
    retry: false,
  });
  const hostingerTemplatesQuery = useQuery({
    queryKey: ['hostinger', 'templates'],
    queryFn: servers.hostingerTemplates,
    enabled: form.provider === 'hostinger',
    retry: false,
  });
  const hostingerDataCentersQuery = useQuery({
    queryKey: ['hostinger', 'data-centers'],
    queryFn: servers.hostingerDataCenters,
    enabled: form.provider === 'hostinger',
    retry: false,
  });
  const hostingerScriptsQuery = useQuery({
    queryKey: ['hostinger', 'post-install-scripts'],
    queryFn: servers.hostingerPostInstallScripts,
    enabled: form.provider === 'hostinger',
    retry: false,
  });

  const lookupError = (err: unknown): string =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    'Konnte Optionen nicht laden — API-Token unter Integrations geprüft?';

  const provisionMutation = useMutation({
    mutationFn: (payload: ProvisionServerPayload) => servers.provision(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Bereitstellung fehlgeschlagen.';
      setError(msg);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const shared = {
      sshPublicKey: form.sshPublicKey || undefined,
      sshPrivateKey: form.sshPrivateKey || undefined,
      sshPassphrase: form.sshPassphrase || undefined,
      maxSlots: form.maxSlots ? parseFloat(form.maxSlots) : undefined,
      notes: form.notes || undefined,
    };

    const payload: ProvisionServerPayload =
      form.provider === 'hetzner'
        ? { provider: 'hetzner', name: form.name, serverType: form.serverType, image: form.image, location: form.location || undefined, ...shared }
        : form.provider === 'aws'
        ? {
            provider: 'aws',
            name: form.name,
            instanceType: form.instanceType,
            region: form.region || undefined,
            diskGb: form.diskGb ? parseInt(form.diskGb) : undefined,
            ...shared,
          }
        : {
            provider: 'hostinger',
            itemId: form.itemId,
            templateId: parseInt(form.templateId),
            dataCenterId: parseInt(form.dataCenterId),
            postInstallScriptId: form.postInstallScriptId ? parseInt(form.postInstallScriptId) : undefined,
            hostname: form.hostname || undefined,
            ...shared,
          };

    provisionMutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface z-10">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Cloud size={16} /> Server automatisch bereitstellen
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Erstellt den Server direkt beim Provider und stößt PREPARE_SERVER an.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Provider</label>
            <select
              value={form.provider}
              onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value as ProvisionForm['provider'] }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="hetzner">Hetzner</option>
              <option value="aws">AWS (EC2)</option>
              <option value="hostinger">Hostinger</option>
            </select>
          </div>

          {form.provider === 'hostinger' && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2.5 rounded-lg text-xs flex gap-2">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>
                Hostinger hat keinen Test-/Sandbox-Modus. „Bereitstellung starten" löst eine echte
                Bestellung gegen das hinterlegte Zahlungsmittel aus — es wird tatsächlich ein VPS gekauft.
              </span>
            </div>
          )}

          {form.provider === 'aws' && (
            <div className="grid grid-cols-2 gap-3">
              {awsOptionsQuery.data && !awsOptionsQuery.data.credentialsOk && (
                <div className="col-span-2 bg-red-50 border border-red-200 text-red-800 px-3 py-2.5 rounded-lg text-xs flex gap-2">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                  <span>
                    Der AWS-Zugang trägt nicht: {awsOptionsQuery.data.credentialsError}. Unter
                    Einstellungen → Integrationen hinterlegen. Die nötigen Rechte stehen in
                    <span className="font-mono"> docs/AWS-IAM-RECHTE.json</span>.
                  </span>
                </div>
              )}
              <div className="col-span-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2.5 rounded-lg text-xs flex gap-2">
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <span>
                  „Bereitstellung starten" startet eine echte EC2-Instanz, die ab der ersten Minute
                  kostet. Die Maschine bekommt die Markierung <span className="font-mono">juni:managed-by</span> —
                  nur solche darf der PC später wieder beenden.
                </span>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.nameRequired')}</label>
                <div className="flex items-center gap-2">
                  <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t('forms.exampleNaboo')} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <NameSuggestionButton onPick={(n) => setForm((f) => ({ ...f, name: n }))} />
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.sizeRequired')}</label>
                <select required value={form.instanceType} onChange={(e) => setForm((f) => ({ ...f, instanceType: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  {awsOptionsQuery.data?.instanceTypes.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name} — {t.vcpu} vCPU, {t.ramGb} GB · ca. {t.approxEurMonth} €/Monat · {t.eignung}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Eine gepflegte Auswahl statt der über 700 Typen der API. Preise sind Anhaltswerte für
                  Frankfurt ohne Platte und Verkehr — zur Größenordnung, nicht zur Abrechnung.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Region</label>
                <select value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    Standard ({awsOptionsQuery.data?.defaultRegion ?? 'eu-central-1'})
                  </option>
                  {awsOptionsQuery.data?.regions.map((r) => (
                    <option key={r.id} value={r.id}>{r.label} ({r.id})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.diskGb')}</label>
                <input type="number" min={20} max={2000} value={form.diskGb}
                  onChange={(e) => setForm((f) => ({ ...f, diskGb: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                <p className="text-xs text-slate-500 mt-1">{t('forms.gp3Note')}</p>
              </div>

              <div className="col-span-2 text-xs text-slate-500 border-t border-slate-200 pt-3">
                Abbild und Zugangsregeln setzt der PC selbst: Ubuntu 24.04 in der jeweils aktuellen
                Fassung (aus Canonicals Parameterspeicher, nicht aus einer gepflegten Id-Liste), und
                eine Security Group mit SSH nur von den Adressen aus den Einstellungen. Die
                Grundausstattung läuft als cloud-init beim ersten Hochfahren.
              </div>
            </div>
          )}

          {form.provider === 'hetzner' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.nameRequired')}</label>
                <div className="flex items-center gap-2">
                  <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t('forms.exampleHoth')} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <NameSuggestionButton onPick={(n) => setForm((f) => ({ ...f, name: n }))} />
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  Wird bei Hetzner als Servername gesetzt und im PC als Hostname geführt.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.serverTypeRequired')}</label>
                <select required value={form.serverType} onChange={(e) => setForm((f) => ({ ...f, serverType: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    {hetznerServerTypesQuery.isLoading ? 'Lade…' : 'Bitte wählen'}
                  </option>
                  {hetznerServerTypesQuery.data?.map((t) => (
                    <option key={t.id} value={t.name}>
                      {formatHetznerServerTypeLabel(t)}
                    </option>
                  ))}
                </select>
                {hetznerServerTypesQuery.isError && (
                  <p className="text-xs text-red-500 mt-1">{lookupError(hetznerServerTypesQuery.error)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.imageRequired')}</label>
                <input required value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
                  placeholder="ubuntu-24.04" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
                <p className="text-xs text-slate-400 mt-1">{t('forms.noImageLookup')}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <select value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    {hetznerLocationsQuery.isLoading ? 'Lade…' : 'Beliebig / Standard'}
                  </option>
                  {hetznerLocationsQuery.data?.map((l) => (
                    <option key={l.id} value={l.name}>
                      {l.city}, {l.country} ({l.name})
                    </option>
                  ))}
                </select>
                {hetznerLocationsQuery.isError && (
                  <p className="text-xs text-red-500 mt-1">{lookupError(hetznerLocationsQuery.error)}</p>
                )}
              </div>

              {(() => {
                const selectedType = hetznerServerTypesQuery.data?.find((t) => t.name === form.serverType);
                if (!selectedType) return null;

                const priceAtLocation = form.location
                  ? selectedType.prices.find((p) => p.location === form.location)
                  : cheapestHetznerPrice(selectedType)
                    ? selectedType.prices.find((p) => p.location === cheapestHetznerPrice(selectedType)!.location)
                    : undefined;

                if (form.location && !priceAtLocation) {
                  return (
                    <div className="col-span-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-xs">
                      {selectedType.name} ist in Location „{form.location}" nicht verfügbar — bitte andere Kombination wählen.
                    </div>
                  );
                }

                if (!priceAtLocation) return null;

                return (
                  <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm">
                    <p className="font-medium text-slate-700">
                      {selectedType.name}{!form.location && ' (günstigste Location)'} — {formatHetznerSpecs(selectedType)}
                    </p>
                    <p className="text-slate-600 mt-1">
                      {parseFloat(priceAtLocation.price_monthly.gross).toFixed(2)} €/Monat ·{' '}
                      {parseFloat(priceAtLocation.price_hourly.gross).toFixed(4)} €/Std ·{' '}
                      {bytesToTb(priceAtLocation.included_traffic)} TB Traffic inklusive
                      {' '}({priceAtLocation.location})
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
          {form.provider === 'hostinger' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.vpsPlanRequired')}</label>
                <select required value={form.itemId} onChange={(e) => setForm((f) => ({ ...f, itemId: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    {hostingerCatalogQuery.isLoading ? 'Lade…' : 'Bitte wählen'}
                  </option>
                  {hostingerCatalogQuery.data?.flatMap((item) =>
                    item.prices.map((price) => (
                      <option key={price.id} value={price.id}>
                        {formatHostingerPriceLabel(item, price)}
                      </option>
                    )),
                  )}
                </select>
                {hostingerCatalogQuery.isError && (
                  <p className="text-xs text-red-500 mt-1">{lookupError(hostingerCatalogQuery.error)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.osTemplateRequired')}</label>
                <select required value={form.templateId} onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    {hostingerTemplatesQuery.isLoading ? 'Lade…' : 'Bitte wählen'}
                  </option>
                  {hostingerTemplatesQuery.data?.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {hostingerTemplatesQuery.isError && (
                  <p className="text-xs text-red-500 mt-1">{lookupError(hostingerTemplatesQuery.error)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.dataCenterRequired')}</label>
                <select required value={form.dataCenterId} onChange={(e) => setForm((f) => ({ ...f, dataCenterId: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    {hostingerDataCentersQuery.isLoading ? 'Lade…' : 'Bitte wählen'}
                  </option>
                  {hostingerDataCentersQuery.data?.map((d) => (
                    <option key={d.id} value={d.id}>{d.city}, {d.continent} ({d.name})</option>
                  ))}
                </select>
                {hostingerDataCentersQuery.isError && (
                  <p className="text-xs text-red-500 mt-1">{lookupError(hostingerDataCentersQuery.error)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Post-Install-Skript</label>
                <p className="text-xs text-slate-500 mb-1">
                  Leer lassen. Der PC hinterlegt beim Kauf seine eigene Grundausstattung im
                  Hostinger-Konto und wählt sie aus — Docker, unser SSH-Schlüssel, Firewall,
                  /opt/juni. Hier etwas zu wählen ist nur der Rückfall, falls das Hochladen
                  scheitert.
                </p>
                <select value={form.postInstallScriptId} onChange={(e) => setForm((f) => ({ ...f, postInstallScriptId: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">
                    {hostingerScriptsQuery.isLoading ? 'Lade…' : 'Grundausstattung des PC (empfohlen)'}
                  </option>
                  {hostingerScriptsQuery.data?.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {hostingerScriptsQuery.isError && (
                  <p className="text-xs text-red-500 mt-1">{lookupError(hostingerScriptsQuery.error)}</p>
                )}
                {hostingerScriptsQuery.data?.length === 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Noch keins vorhanden — zuerst über die Hostinger-API/hPanel eins anlegen (z.B. ein No-op-Script).
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Hostname</label>
                <div className="flex items-center gap-2">
                  <input value={form.hostname} onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
                    placeholder={t('forms.exampleHoth')}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <NameSuggestionButton onPick={(n) => setForm((f) => ({ ...f, hostname: n }))} />
                </div>
              </div>
            </div>
          )}

          {/* ══ DER SCHLUESSEL, MIT GENERATOR ═══════════════════════════════
              Hier standen drei nackte Textfelder. Der Generator gab es nur im
              Dialog „Server hinzufuegen" — nicht weil jemand ihn hier nicht
              wollte, sondern weil `SshKeySection` auf `ServerForm` getippt war
              und dieses Formular `ProvisionForm` benutzt.

              Das war folgenreicher, als es aussieht: wer beim Kauf keinen
              Schluessel zur Hand hatte, liess die Felder leer. Der Worker
              erzeugt dann zwar einen (`server-bootstrap.worker.ts`), legt ihn
              aber nur verschluesselt im PC ab — kein Mensch bekommt ihn je zu
              sehen. Auf so eine Maschine kommt danach ausschliesslich der PC.

              Mit dem Generator hier faellt der private Schluessel einmal
              sichtbar an und laesst sich herunterladen. */}
          <SshKeySection
            form={form}
            setForm={setForm}
            bezeichnung={form.name || form.hostname}
            onPublicKey={(pub) => setForm((f) => ({ ...f, sshPublicKey: pub }))}
          />

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {t('forms.publicKey')}
              <span className="ml-1 font-normal text-slate-400">
                — wird beim Erzeugen gefüllt
              </span>
            </label>
            <textarea rows={2} value={form.sshPublicKey} onChange={(e) => setForm((f) => ({ ...f, sshPublicKey: e.target.value }))}
              placeholder="ssh-ed25519 AAAA... juni-pc" spellCheck={false}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none bg-surface" />
            <p className="text-xs text-slate-400 mt-1">
              Wird beim Anbieter hinterlegt. Leer lassen ist in Ordnung — dann leitet der
              Kaufvorgang ihn aus dem privaten Schlüssel ab. Umgekehrt geht es nicht:
              <strong className="font-medium"> ohne privaten Schlüssel kommt der PC nach dem Kauf nie auf die Maschine.</strong>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.maxSlots')}</label>
              <input type="number" value={form.maxSlots} onChange={(e) => setForm((f) => ({ ...f, maxSlots: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notizen</label>
              <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600">
              Abbrechen
            </button>
            <button type="submit" disabled={provisionMutation.isPending}
              className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-60 flex items-center gap-2 ${
                form.provider === 'hostinger' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-brand-600 hover:bg-brand-700'
              }`}>
              {provisionMutation.isPending && <Loader2 size={14} className="animate-spin" />}
              {form.provider === 'hostinger' ? 'Jetzt kaufen & bereitstellen' : 'Bereitstellung starten'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

/**
 * Hands out the next free name from the house convention (Star Wars planets).
 *
 * A suggestion, never a rule: a server the customer provides keeps its real
 * domain, and sometimes a machine wants a name that says what it does. Clicking
 * again walks further down the list — the first suggestion is the best-known
 * name, not the only one.
 */
function NameSuggestionButton({ onPick }: { onPick: (name: string) => void }) {
  const [index, setIndex] = useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ['server-name-suggestion'],
    queryFn: servers.nameSuggestion,
    staleTime: 60_000,
  });

  const free = data?.free ?? [];
  const next = free[index % Math.max(free.length, 1)];

  return (
    <button
      type="button"
      onClick={() => {
        if (!next) return;
        onPick(next);
        setIndex((i) => i + 1);
      }}
      disabled={isLoading || free.length === 0}
      title={
        free.length === 0
          ? 'Keine freien Namen mehr in der Liste'
          : `Nächster freier Name aus der Hausliste — noch ${free.length} frei. Nochmal klicken für den übernächsten.`
      }
      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
    >
      {isLoading ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
      {next ? next : 'Vorschlag'}
    </button>
  );
}

// ─── Base stacks on a new server ──────────────────────────────────────────────

interface RolloutItem {
  id: string;
  name: string;
  state: 'pending' | 'running' | 'ok' | 'failed';
  error?: string;
}

interface Rollout {
  serverId: string;
  hostname: string;
  hasSshKey: boolean;
  items: RolloutItem[];
}

/**
 * Which base stacks to roll out right after the server is created.
 *
 * Pre-checked from `isDefault` — "on nearly every host" is what that flag means.
 * Every host is still allowed to be the exception, so nothing is forced, and a
 * failed stack later on does not undo the server.
 */
function StackChecklist({
  selected,
  setSelected,
}: {
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { data: stacks = [], isLoading } = useQuery({
    queryKey: ['base-stacks'],
    queryFn: baseStacks.list,
    staleTime: 60_000,
  });

  // Only on the first load of this dialog opening. Re-running on every refetch
  // would silently re-check boxes the operator had just unticked.
  const initialised = useRef(false);
  useEffect(() => {
    if (initialised.current || stacks.length === 0) return;
    initialised.current = true;
    setSelected(stacks.filter((s) => s.isDefault && s.status === 'ACTIVE').map((s) => s.id));
  }, [stacks, setSelected]);

  const available = stacks.filter((s) => s.status !== 'DEPRECATED');

  if (isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-400">
        <Loader2 size={12} className="animate-spin" /> Base-Stacks werden geladen…
      </p>
    );
  }

  if (available.length === 0) {
    return (
      <p className="text-xs text-slate-400">
        Keine Base-Stacks hinterlegt. Unter PC-Einstellungen → Base-Stacks lassen sich
        Composes zentral anlegen.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {available.map((stack: BaseStack) => {
        const checked = selected.includes(stack.id);
        return (
          <label
            key={stack.id}
            className={clsx(
              'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2',
              checked ? 'border-brand-300 bg-brand-50/50' : 'border-slate-200 bg-surface',
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) =>
                setSelected((prev) =>
                  e.target.checked ? [...prev, stack.id] : prev.filter((id) => id !== stack.id),
                )
              }
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-slate-800">{stack.name}</span>
                <span className="font-mono text-[11px] text-slate-400">{stack.slug}</span>
                {stack.isDefault && (
                  <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[11px] font-medium text-brand-700">
                    Standard
                  </span>
                )}
                {stack.status === 'DRAFT' && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                    Entwurf
                  </span>
                )}
              </span>
              {stack.description && (
                <span className="mt-0.5 block text-xs leading-snug text-slate-500">
                  {stack.description}
                </span>
              )}
              {stack.declaredPorts.length > 0 && (
                <span className="mt-0.5 block font-mono text-[11px] text-slate-400">
                  Ports {stack.declaredPorts.join(', ')}
                </span>
              )}
            </span>
          </label>
        );
      })}
      <p className="pt-0.5 text-[11px] leading-snug text-slate-400">
        Wird nach dem Anlegen der Reihe nach ausgerollt — nicht parallel, weil sonst zwei
        Composes gleichzeitig dieselben Netzwerke anlegen. Variablen kommen aus den
        Ableitungen des Servernamens; überschreiben lässt sich das danach je Host unter
        „Base-Stacks".
      </p>
    </div>
  );
}

/**
 * Progress of the post-creation rollout.
 *
 * Every stack reports on its own: one failure is shown and the queue continues,
 * because the server exists either way and a half-rolled-out host is a state
 * someone has to be able to see.
 */
function RolloutModal({
  rollout,
  onClose,
  onOpenPanel,
  onCheckHost,
}: {
  rollout: Rollout;
  onClose: () => void;
  onOpenPanel: () => void;
  onCheckHost: () => void;
}) {
  const { t } = useTranslation();
  const done = rollout.items.every((i) => i.state === 'ok' || i.state === 'failed');
  const failed = rollout.items.filter((i) => i.state === 'failed');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">{t('servers.rolloutBaseStacks')}</h2>
          <p className="font-mono text-xs text-slate-500">{rollout.hostname}</p>
        </div>
        <div className="space-y-2 px-6 py-5">
          {rollout.items.map((item) => (
            <div key={item.id} className="flex items-start gap-2">
              {item.state === 'running' && (
                <Loader2 size={14} className="mt-0.5 shrink-0 animate-spin text-brand-600" />
              )}
              {item.state === 'ok' && (
                <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-600" />
              )}
              {item.state === 'failed' && (
                <XCircle size={14} className="mt-0.5 shrink-0 text-red-600" />
              )}
              {item.state === 'pending' && (
                <Circle size={14} className="mt-0.5 shrink-0 text-slate-300" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{item.name}</div>
                {item.state === 'running' && (
                  <div className="text-xs text-slate-500">
                    Images werden gezogen, das dauert beim ersten Mal ein paar Minuten…
                  </div>
                )}
                {item.error && (
                  <div className="text-xs leading-snug text-red-600">{item.error}</div>
                )}
              </div>
            </div>
          ))}

          {done && failed.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-800">
              Der Server ist angelegt und bleibt es —{' '}
              {failed.length === 1 ? 'ein Stack' : `${failed.length} Stacks`} konnten nicht
              ausgerollt werden. Unter „Base-Stacks" beim Server steht die Ausgabe, dort lässt
              sich auch erneut oder trotz Blocker ausrollen. Wenn alle gescheitert sind, liegt
              es meist am Host selbst — „Host prüfen" sagt, woran.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          {done && failed.length > 0 && (
            <button
              onClick={onCheckHost}
              className="mr-auto rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Host prüfen
            </button>
          )}
          <button
            onClick={onOpenPanel}
            disabled={!done}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Base-Stacks öffnen
          </button>
          <button
            onClick={onClose}
            disabled={!done}
            className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {done ? 'Fertig' : 'Läuft…'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServersPage() {
  const { t } = useTranslation();
  const [sicht, setSicht] = useEinstellung<ListenSicht>('server.sicht', 'aktiv');
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  // Set while editing; null means the dialog is creating a new server. The same
  // form serves both so the fields, defaults and ownership rules cannot drift
  // apart between "add" and "edit".
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dockerFor, setDockerFor] = useState<ServerWithSsh | null>(null);
  const [proxyZugangFuer, setProxyZugangFuer] = useState<ServerWithSsh | null>(null);
  const [aufraeumen, setAufraeumen] = useState<{
    server: string;
    laeuft: boolean;
    bericht: AufraeumBericht | null;
    fehler: string | null;
  } | null>(null);

  /**
   * Alte Images wegräumen — erst schätzen, dann fragen, dann tun.
   *
   * Die Schätzung ist nicht Höflichkeit, sondern nötig: bei einem Wirt mit
   * wenig Altlast lohnt der Vorgang nicht, und bei einem mit viel will man
   * vorher wissen, worauf man sich einlässt (es dauert Minuten).
   */
  async function imagesAufraeumen(server: ServerWithSsh) {
    setAufraeumen({ server: server.hostname, laeuft: true, bericht: null, fehler: null });
    try {
      const vorschau = await servers.imagesAufraeumen(server.id, { nurVorschau: true });
      const zuHolen = vorschau.freigegeben ?? 'unbekannt';

      if (
        !confirm(
          `Auf ${server.hostname} sind ${vorschau.vorher.images ?? '?'} Images abgelegt ` +
            `(${vorschau.vorher.groesse ?? '?'}), davon wiederverwertbar: ${zuHolen}. ` +
            `Dazu Build-Cache: ${vorschau.buildCache ?? '0B'}.\n\n` +
            'Entfernt werden nur Images, die älter als 14 Tage sind und von keinem ' +
            'Container benutzt werden. Laufende Container sind nicht betroffen.\n\n' +
            'Das kann einige Minuten dauern. Jetzt aufräumen?',
        )
      ) {
        setAufraeumen(null);
        return;
      }

      const bericht = await servers.imagesAufraeumen(server.id, { tage: 14 });
      setAufraeumen({ server: server.hostname, laeuft: false, bericht, fehler: null });
    } catch (err) {
      const f = err as { response?: { data?: { message?: string; error?: string } } };
      setAufraeumen({
        server: server.hostname,
        laeuft: false,
        bericht: null,
        fehler: f?.response?.data?.message ?? f?.response?.data?.error ?? 'Aufräumen fehlgeschlagen',
      });
    }
  }
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [form, setForm] = useState<ServerForm>(DEFAULT_FORM);
  const [formError, setFormError] = useState('');
  const [replaceKeyFor, setReplaceKeyFor] = useState<ServerWithSsh | null>(null);
  const [selectedStacks, setSelectedStacks] = useState<string[]>([]);
  const [rollout, setRollout] = useState<Rollout | null>(null);
  // Not a ServerWithSsh: the panel is also opened straight after a rollout, when
  // the servers query has not necessarily come back with the new row yet.
  const [stacksFor, setStacksFor] = useState<{
    id: string;
    hostname: string;
    hasSshKey: boolean;
  } | null>(null);
  const [checkFor, setCheckFor] = useState<{ id: string; hostname: string } | null>(null);
  const [aufnahmeFor, setAufnahmeFor] = useState<{ id: string; hostname: string } | null>(null);
  const [snapshotFor, setSnapshotFor] = useState<{ id: string; hostname: string } | null>(null);
  const [showRobotImport, setShowRobotImport] = useState(false);
  const [metrikFor, setMetrikFor] = useState<{ id: string; hostname: string } | null>(null);
  /** What the server was called when the edit dialog opened — see the rename note. */
  const [originalHostname, setOriginalHostname] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['servers'],
    queryFn: servers.list,
  });

  /**
   * Rolls the ticked stacks out one after another.
   *
   * Sequential on purpose: two composes starting at once race each other on
   * `docker network create` and on the daemon itself. Errors are collected rather
   * than thrown — the server is already created, and aborting the queue would
   * leave the remaining stacks silently unrolled.
   */
  async function runRollout(server: Server, chosen: BaseStack[], hasSshKey: boolean) {
    setRollout({
      serverId: server.id,
      hostname: server.hostname,
      hasSshKey,
      items: chosen.map((s) => ({ id: s.id, name: s.name, state: 'pending' })),
    });

    const patch = (id: string, next: Partial<RolloutItem>) =>
      setRollout((r) =>
        r ? { ...r, items: r.items.map((i) => (i.id === id ? { ...i, ...next } : i)) } : r,
      );

    for (const stack of chosen) {
      patch(stack.id, { state: 'running' });
      try {
        const result = await baseStacks.deploy(stack.id, server.id, {}, false);
        patch(stack.id, {
          state: result.ok ? 'ok' : 'failed',
          error: result.ok ? undefined : result.error ?? 'Ausrollen fehlgeschlagen.',
        });
      } catch (err) {
        const response = (err as {
          response?: { status?: number; data?: { message?: string; preflight?: PreflightResult } };
        }).response;
        // A pre-flight veto is not forced through here — that decision belongs in
        // the panel, where the operator can see what exactly is in the way.
        const blockers = response?.data?.preflight?.issues
          .filter((i) => i.level === 'error')
          .map((i) => i.message)
          .join(' ');
        patch(stack.id, {
          state: 'failed',
          error: blockers || response?.data?.message || 'Ausrollen fehlgeschlagen.',
        });
      }
    }

    void qc.invalidateQueries({ queryKey: ['server-base-stacks', server.id] });
  }

  const createMutation = useMutation({
    mutationFn: (payload: Partial<Server> & { sshPrivateKey?: string; sshPassphrase?: string }) =>
      servers.create(payload),
    onSuccess: (server, variables) => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      setShowModal(false);
      setForm(DEFAULT_FORM);

      const chosen = (qc.getQueryData<BaseStack[]>(['base-stacks']) ?? []).filter((s) =>
        selectedStacks.includes(s.id),
      );
      setSelectedStacks([]);
      // Without a key there is nothing to deploy over, and the checklist has
      // already been replaced by the note saying so — so a stale selection from
      // before the key was cleared is dropped rather than failing eight times.
      if (chosen.length > 0 && variables.sshPrivateKey) {
        void runRollout(server, chosen, true);
      }
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Fehler beim Hinzufügen des Servers.';
      setFormError(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      servers.update(id, payload as Partial<Server>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      setShowModal(false);
      setEditingId(null);
      setForm(DEFAULT_FORM);
    },
    onError: (err: unknown) => {
      setFormError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Fehler beim Speichern des Servers.',
      );
    },
  });

  /**
   * Nur die Farbe, aus der Tabelle heraus.
   *
   * Eigene Mutation statt `updateMutation`: die schliesst bei Erfolg den
   * Dialog und leert das Formular. Aus der Zeile heraus gibt es beides nicht —
   * sie wuerde damit stillschweigend eine offene Bearbeitung wegraeumen.
   */
  const farbeMutation = useMutation({
    mutationFn: ({ id, farbe }: { id: string; farbe: string | null }) =>
      servers.update(id, { farbe } as Partial<Server>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
    },
  });

  const decommissionMutation = useMutation({
    mutationFn: (id: string) => servers.decommission(id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      if (result.warning) alert(result.warning);
    },
  });

  const sshKeyLoeschen = useMutation({
    mutationFn: (id: string) => servers.deleteSshKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Entfernen fehlgeschlagen.'),
  });

  const purgeMutation = useMutation({
    mutationFn: (id: string) => servers.purge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['servers'] }),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : "Löschen fehlgeschlagen."),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    // A dedicated or customer-provided box without a customer is not a thing —
    // the whole point of those two modes is the assignment.
    if (form.ownership !== 'SHARED' && !form.customerId) {
      setFormError('Bitte einen Kunden zuordnen — dedizierte und kundeneigene Server gehören immer zu genau einem Kunden.');
      return;
    }

    const payload = {
      ownership: form.ownership,
      customerId: form.ownership === 'SHARED' ? null : form.customerId,
      providerAccountRef: form.providerAccountRef || undefined,
      hostname: form.hostname,
      ipAddress: form.ipAddress,
      provider: form.provider,
      region: form.region || undefined,
      sshUser: form.sshUser || 'root',
      sshPort: form.sshPort ? parseInt(form.sshPort) : 22,
      status: (form.status as Server['status']) || undefined,
      cpuCores: form.cpuCores ? parseInt(form.cpuCores) : undefined,
      // Leeres Feld heisst ausdruecklich "keine GPU", nicht "unveraendert" —
      // sonst liesse sich ein falsch eingetragener Wert nie wieder entfernen.
      gpuModel: form.gpuModel.trim() || null,
      gpuVramMb: form.gpuVramMb ? parseInt(form.gpuVramMb) : null,
      gpuCount: form.gpuCount ? parseInt(form.gpuCount) : null,
      ramMb: form.ramMb ? parseInt(form.ramMb) : undefined,
      diskGb: form.diskGb ? parseInt(form.diskGb) : undefined,
      maxSlots: form.maxSlots ? parseFloat(form.maxSlots) : undefined,
      costPerMonth: form.costPerMonth || undefined,
      notes: form.notes || undefined,

      farbe: form.farbe || null,
      sshPrivateKey: form.sshPrivateKey || undefined,
      sshPassphrase: form.sshPassphrase || undefined,
    } as Partial<Server> & Record<string, unknown>;

    if (editingId) {
      // The key is changed through its own dialog ("Ersetzen"), so an edit never
      // carries one — sending an empty value would otherwise look like a request
      // to clear it.
      const { sshPrivateKey: _k, sshPassphrase: _p, ...rest } = payload as Record<string, unknown>;
      updateMutation.mutate({ id: editingId, payload: rest });
      return;
    }
    createMutation.mutate(payload);
  }

  /** Fills the dialog from an existing server. */
  function startEdit(server: ServerWithSsh) {
    setForm({
      farbe: server.farbe ?? '',
      ownership: (server.ownership ?? 'SHARED') as Ownership,
      customerId: server.customerId ?? '',
      providerAccountRef: server.providerAccountRef ?? '',
      hostname: server.hostname,
      ipAddress: server.ipAddress,
      provider: server.provider,
      region: server.region ?? '',
      sshUser: server.sshUser,
      sshPort: String(server.sshPort),
      status: server.status,
      cpuCores: server.cpuCores ? String(server.cpuCores) : '',
      gpuModel: server.gpuModel ?? '',
      gpuVramMb: server.gpuVramMb ? String(server.gpuVramMb) : '',
      gpuCount: server.gpuCount ? String(server.gpuCount) : '',
      ramMb: server.ramMb ? String(server.ramMb) : '',
      diskGb: server.diskGb ? String(server.diskGb) : '',
      maxSlots: String(server.maxSlots),
      costPerMonth: server.costPerMonth ? String(server.costPerMonth) : '',
      notes: server.notes ?? '',
      sshPrivateKey: '',
      sshPassphrase: '',
    });
    setEditingId(server.id);
    setOriginalHostname(server.hostname);
    setFormError('');
    setShowModal(true);
  }

  const rows = (data ?? []) as ServerWithSsh[];

  // Sortierbare Spalten. Der Wert ist das, wonach sortiert wird — nicht das,
  // was in der Zelle steht: bei der Größenspalte wäre das ein Text, und
  // dann stünde 16 GB vor 4 GB.
  const spalten: SpaltenDefinition<ServerWithSsh>[] = [
    { key: 'hostname', label: t('tables.hostname'), wert: (s) => s.hostname },
    { key: 'ip', label: t('tables.ip'), wert: (s) => s.ipAddress },
    { key: 'zuordnung', label: t('tables.assignment'), wert: (s) => s.ownership },
    { key: 'status', label: t('common.status'), wert: (s) => s.status },
    { key: 'ssh', label: t('tables.sshKey'), wert: (s) => (s.ssh?.hasKey ? 1 : 0) },
    { key: 'slots', label: t('tables.slots'), wert: (s) => s.maxSlots ?? 0, numerisch: true },
    { key: 'groesse', label: t('tables.hardware'), wert: (s) => s.ramMb ?? 0, numerisch: true },
    { key: 'angelegt', label: t('tables.created'), wert: (s) => s.createdAt },
  ];

  const filter: FilterDefinition<ServerWithSsh>[] = [
    {
      key: 'status',
      label: t('common.status'),
      optionen: [
        { wert: '!DECOMMISSIONED', label: t('tables.exceptDecommissioned') },
        { wert: 'READY', label: 'Bereit' },
        { wert: 'ACTIVE', label: 'Aktiv' },
        { wert: 'PREPARING', label: 'In Vorbereitung' },
        { wert: 'PROVISIONING', label: 'Wird angelegt' },
        { wert: 'MAINTENANCE', label: 'Wartung' },
        { wert: 'FAILED', label: 'Fehlgeschlagen' },
        { wert: 'DECOMMISSIONED', label: 'Stillgelegt' },
      ],
      // Der häufigste Wunsch ist nicht ein einzelner Status, sondern das
      // Ausblenden der stillgelegten. Deshalb die Verneinung als eigene Option
      // statt sieben Häkchen.
      passt: (s, w) => (w.startsWith('!') ? s.status !== w.slice(1) : s.status === w),
    },
    {
      key: 'anbieter',
      label: t('tables.provider'),
      optionen: [...new Set(rows.map((r) => r.provider).filter(Boolean))].map((p) => ({
        wert: String(p),
        label: String(p),
      })),
      passt: (s, w) => s.provider === w,
    },
    {
      key: 'zuordnung',
      label: t('tables.assignment'),
      optionen: [
        { wert: 'SHARED', label: t('tables.shared') },
        { wert: 'DEDICATED_JUNI', label: t('tables.dedicated') },
        { wert: 'CUSTOMER_PROVIDED', label: t('tables.customerOwned') },
      ],
      passt: (s, w) => s.ownership === w,
    },
  ];

  /**
   * Ausser Betrieb heisst bei einem Server: abgemeldet.
   *
   * `DRAINING`, `MAINTENANCE`, `FULL` und `FAILED` sind Betriebszustaende, die
   * Aufmerksamkeit brauchen — die gehoeren in die aktive Sicht, gerade weil sie
   * unangenehm sind.
   */
  const ABGEMELDET: string[] = ['DECOMMISSIONED'];
  const { gefiltert, zaehler } = nachSicht(
    rows,
    (srv) => !ABGEMELDET.includes(srv.status),
    sicht,
  );

  const tabelle = useTabelle<ServerWithSsh>({
    speichern: 'server',
    zeilen: gefiltert,
    spalten,
    filter,
    suchfelder: (s) => [s.hostname, s.ipAddress, s.provider, s.serverType, s.region, s.notes],
    startSortierung: { key: 'hostname', richtung: 'auf' },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{tabelle.sichtbar.length === tabelle.gesamt ? tabelle.gesamt + ' Server' : tabelle.sichtbar.length + ' von ' + tabelle.gesamt + ' Servern'}</p>
        <div className="flex items-center gap-2">
          <StandardsKnopf bereich="server" />
          <button
            onClick={() => setShowProvisionModal(true)}
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-surface text-sm font-medium px-3.5 py-2 rounded-lg"
          >
            <Cloud size={15} /> Automatisch bereitstellen
          </button>
          {/* Getrennter Knopf, nicht im Bereitstellen-Dialog: dort wird eine
              Maschine gekauft, hier eine vorhandene übernommen. Das ist ein
              anderer Vorgang mit anderen Folgen. */}
          <button
            onClick={() => setShowRobotImport(true)}
            className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-3.5 py-2 rounded-lg"
          >
            <HardDrive size={15} /> {t('robot.open')}
          </button>
          <button
            onClick={() => {
              setForm(DEFAULT_FORM);
              setEditingId(null);
              setFormError('');
              setShowModal(true);
            }}
            className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg"
          >
            <Plus size={15} /> Server hinzufügen
          </button>
        </div>
      </div>

      <TabellenLeiste
        steuerung={tabelle}
        filter={filter}
        platzhalter={t('tables.searchServers')}
      />

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <ListenReiter sicht={sicht} onWechsel={setSicht} zaehler={zaehler} />
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} filter={filter} extra={['Aktionen']} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : tabelle.sichtbar.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-slate-400 italic">
                  {/* Zwei verschiedene Aussagen: "es gibt keine" und "die Suche
                      trifft keine" sind nicht dasselbe, und wer filtert, sucht
                      sonst den Fehler an der falschen Stelle. */}
                  {rows.length === 0
                    ? 'Noch keine Server. Klicke auf „Server hinzufügen".'
                    : t('tables.noMatch')}
                </td>
              </tr>
            ) : (
              tabelle.sichtbar.map((server) => (
                <tr key={server.id} className="hover:bg-slate-50">
                  {/* Der Punkt vor dem Namen ist zugleich der Weg zur Farbe.
                      Server haben keine Detailseite, auf der er stehen könnte —
                      also hier, an derselben Stelle wie bei den Instanzen. Der
                      Wähler im Dialog bleibt daneben bestehen: beim Anlegen
                      gibt es noch keine Zeile, in der man klicken könnte. */}
                  <td className="px-4 py-3 font-medium text-slate-800"><span className="inline-flex items-center gap-2"><FarbPunktSchalter wert={server.farbe} onWahl={(farbe) => farbeMutation.mutate({ id: server.id, farbe })} titel={server.hostname} />{server.hostname}</span></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">
                    {server.ipAddress}
                    {server.sshUser !== 'root' && (
                      <span className="ml-1 text-slate-400">{server.sshUser}@</span>
                    )}
                    {server.sshPort !== 22 && (
                      <span className="text-slate-400">:{server.sshPort}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <OwnershipCell server={server} />
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={server.status} type="server" /></td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <SshKeyBadge meta={server.ssh} />
                      <button
                        onClick={() => setReplaceKeyFor(server)}
                        className="text-xs text-slate-400 hover:text-brand-600 block"
                      >
                        {server.ssh?.hasKey ? t('servers.replaceKey') : t('servers.storeKey')}
                      </button>
                      {/* Zugang abgeben, ohne den Server zu löschen.
                          Der Fall dafür ist ein kundeneigener Host, dessen
                          Betreuung endet: die Maschine bleibt, unser Schlüssel
                          soll weg. Bisher ging das nur über die API. */}
                      {server.ssh?.hasKey && (
                        <button
                          onClick={() => {
                            if (
                              confirm(
                                t('servers.removeKeyConfirm', { host: server.hostname }),
                              )
                            )
                              sshKeyLoeschen.mutate(server.id);
                          }}
                          className="text-xs text-slate-400 hover:text-red-600 block"
                        >
                          {t('servers.removeKey')}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <SlotBar active={server._count?.instances ?? 0} max={server.maxSlots} />
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {[
                      server.cpuCores ? `${server.cpuCores}c` : null,
                      server.ramMb ? `${server.ramMb < 1024 ? server.ramMb + 'MB' : Math.round(server.ramMb / 1024) + 'GB'}` : null,
                      server.diskGb ? `${server.diskGb}GB` : null,
                    ]
                      .filter(Boolean)
                      .join(' / ') || '—'}
                    {server.gpuModel && (
                      <div className="mt-0.5 text-[11px] text-violet-700">
                        {[
                          server.gpuCount && server.gpuCount > 1 ? `${server.gpuCount}×` : null,
                          server.gpuModel,
                          server.gpuVramMb
                            ? `${Math.round(server.gpuVramMb / 1024)}GB VRAM`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                    {server.createdAt
                      ? new Date(server.createdAt).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: '2-digit',
                        })
                      : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {/* Frueher standen hier acht Verweise untereinander -- bei
                        vier Servern ueber tausend Pixel Tabellenhoehe, und jede
                        neue Funktion machte es schlimmer. Jetzt: ein Knopf, der
                        sein Ergebnis inline zeigt, und ein Menue fuer den Rest. */}
                    <div className="flex items-center gap-1.5">
                      <SshTestButton server={server} kompakt />
                      <AktionsMenue
                        aktionen={[
                          { label: 'Bearbeiten', onClick: () => startEdit(server) },
                          {
                            label: 'Alte Images aufräumen',
                            onClick: () => imagesAufraeumen(server),
                            gesperrt: !server.ssh?.hasKey,
                            titel: server.ssh?.hasKey
                              ? 'Ungenutzte Images älter als 14 Tage und den Build-Cache entfernen'
                              : 'Zuerst SSH-Key hinterlegen',
                          },
                          {
                            label: 'Zugang zum Proxy-Panel',
                            onClick: () => setProxyZugangFuer(server),
                            gesperrt: !server.ssh?.hasKey,
                            titel: server.ssh?.hasKey
                              ? 'Mailadresse und Kennwort des Proxy-Panels setzen'
                              : 'Zuerst SSH-Key hinterlegen',
                          },
                          {
                            label: 'Docker-Host',
                            onClick: () => setDockerFor(server),
                            gesperrt: !server.ssh?.hasKey,
                            titel: server.ssh?.hasKey
                              ? 'Container und Auslastung des Hosts ansehen'
                              : 'Zuerst SSH-Key hinterlegen',
                          },
                          {
                            // Bewusst nie gesperrt: "kein Schluessel hinterlegt"
                            // ist eine der Antworten, die diese Pruefung gibt.
                            label: 'Host prüfen',
                            onClick: () => setCheckFor({ id: server.id, hostname: server.hostname }),
                            titel: t('servers.hostCheckHint'),
                          },
                          {
                            // Ebenfalls nie gesperrt: ob und warum diese Maschine
                            // keine Snapshots kann, sagt erst das Panel.
                            label: t('snapshots.title'),
                            onClick: () => setSnapshotFor({ id: server.id, hostname: server.hostname }),
                          },
                          {
                            label: t('metrics.open'),
                            onClick: () => setMetrikFor({ id: server.id, hostname: server.hostname }),
                          },
                          {
                            label: 'Fremde Stacks aufnehmen',
                            onClick: () => setAufnahmeFor({ id: server.id, hostname: server.hostname }),
                            gesperrt: !server.ssh?.hasKey,
                            titel: server.ssh?.hasKey
                              ? 'Portainer- und andere Compose-Stacks als beobachtete Instanz in die Übersicht holen'
                              : 'Zuerst SSH-Key hinterlegen',
                          },
                          {
                            label: 'Base-Stacks',
                            onClick: () =>
                              setStacksFor({
                                id: server.id,
                                hostname: server.hostname,
                                hasSshKey: Boolean(server.ssh?.hasKey),
                              }),
                            titel: t('servers.baseStackHint'),
                          },
                          ...(server.status !== 'DECOMMISSIONED'
                            ? [
                                {
                                  label: 'Stilllegen',
                                  gefaehrlich: true,
                                  onClick: () => {
                                    // Was wirklich passiert, steht in der
                                    // Rueckfrage. "Stilllegen?" allein
                                    // verschweigt, dass die Maschine bei Hetzner
                                    // und AWS dabei beendet wird -- bei Hostinger
                                    // dagegen nicht, und dort laeuft die Rechnung
                                    // sonst weiter.
                                    const folge =
                                      server.provider === 'hetzner' || server.provider === 'aws'
                                        ? 'Die Maschine wird beim Anbieter beendet. Ihre Daten sind danach weg.'
                                        : server.provider === 'hostinger'
                                          ? 'Hostinger kennt keine Kündigung per Schnittstelle — das Abo musst du im hPanel selbst kündigen, sonst läuft die Abrechnung weiter.'
                                          : server.provider === 'hetzner-robot'
                                            ? 'Dedizierte Maschine: der Datensatz wird stillgelegt, der Vertrag läuft weiter. Kündigung mit Termin in Robot vornehmen.'
                                            : 'Der Server wird im PC stillgelegt. Die Maschine selbst bleibt unberührt.';
                                    if (confirm(`Server „${server.hostname}" stilllegen?\n\n${folge}`))
                                      decommissionMutation.mutate(server.id);
                                  },
                                } as Aktion,
                              ]
                            : !server.providerServerId
                              ? [
                                  {
                                    // Ein Datensatz, hinter dem nie eine Maschine
                                    // stand -- etwa nach einem Kauf, der vor der
                                    // Bestellung scheiterte. Der darf restlos weg.
                                    label: 'Datensatz löschen',
                                    gefaehrlich: true,
                                    onClick: () => {
                                      if (
                                        confirm(
                                          `Datensatz „${server.hostname}" endgültig löschen?\n\n` +
                                            'Zu diesem Eintrag gibt es keine Maschine beim Anbieter — er ist eine Karteileiche. ' +
                                            'Der Eintrag verschwindet samt hinterlegter Zugangsdaten.',
                                        )
                                      )
                                        purgeMutation.mutate(server.id);
                                    },
                                  } as Aktion,
                                ]
                              : []),
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste blaettern={tabelle.blaettern} />
      {aufraeumen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-surface p-5 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">
              Images aufräumen — {aufraeumen.server}
            </h2>

            {aufraeumen.laeuft && (
              <p className="mt-3 text-sm text-slate-600">
                Läuft … Bei vielen alten Images dauert das einige Minuten. Laufende
                Container sind davon nicht betroffen.
              </p>
            )}

            {aufraeumen.fehler && (
              <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {aufraeumen.fehler}
              </p>
            )}

            {aufraeumen.bericht && (
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <p>
                  <span className="font-semibold text-emerald-700">
                    {aufraeumen.bericht.freigegeben}
                  </span>{' '}
                  durch Images freigegeben
                  {aufraeumen.bericht.buildCache &&
                    aufraeumen.bericht.buildCache !== '0B' &&
                    `, dazu ${aufraeumen.bericht.buildCache} Build-Cache`}
                  .
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="text-left font-medium">&nbsp;</th>
                      <th className="text-right font-medium">vorher</th>
                      <th className="text-right font-medium">nachher</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    <tr>
                      <td className="py-0.5">Platte belegt</td>
                      <td className="text-right">{aufraeumen.bericht.vorher.belegtProzent ?? '?'} %</td>
                      <td className="text-right font-semibold text-emerald-700">
                        {aufraeumen.bericht.nachher.belegtProzent ?? '?'} %
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5">frei</td>
                      <td className="text-right">{aufraeumen.bericht.vorher.frei ?? '?'}</td>
                      <td className="text-right font-semibold text-emerald-700">
                        {aufraeumen.bericht.nachher.frei ?? '?'}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Images</td>
                      <td className="text-right">{aufraeumen.bericht.vorher.images ?? '?'}</td>
                      <td className="text-right">{aufraeumen.bericht.nachher.images ?? '?'}</td>
                    </tr>
                  </tbody>
                </table>
                {aufraeumen.bericht.containerLaufend !== null && (
                  <p className="text-xs text-slate-500">
                    {aufraeumen.bericht.containerLaufend} Container laufen weiterhin.
                  </p>
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setAufraeumen(null)}
                disabled={aufraeumen.laeuft}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
              >
                {aufraeumen.laeuft ? 'Bitte warten …' : 'Schließen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {dockerFor && (
        <DockerHostPanel
          serverId={dockerFor.id}
          hostname={dockerFor.hostname}
          onClose={() => setDockerFor(null)}
        />
      )}

      {stacksFor && (
        <BaseStackPanel
          serverId={stacksFor.id}
          hostname={stacksFor.hostname}
          hasSshKey={stacksFor.hasSshKey}
          onClose={() => setStacksFor(null)}
        />
      )}

      {aufnahmeFor && (
        <StackAufnahmePanel
          serverId={aufnahmeFor.id}
          hostname={aufnahmeFor.hostname}
          onClose={() => setAufnahmeFor(null)}
        />
      )}

      {checkFor && (
        <HostCheckPanel
          serverId={checkFor.id}
          hostname={checkFor.hostname}
          onClose={() => setCheckFor(null)}
        />
      )}

      {showRobotImport && <RobotImportModal onClose={() => setShowRobotImport(false)} />}
      {proxyZugangFuer && (
        <ProxyZugangModal
          serverId={proxyZugangFuer.id}
          hostname={proxyZugangFuer.hostname}
          onClose={() => setProxyZugangFuer(null)}
        />
      )}

      {metrikFor && (
        <MetrikPanel
          serverId={metrikFor.id}
          titel={metrikFor.hostname}
          onClose={() => setMetrikFor(null)}
        />
      )}

      {snapshotFor && (
        <SnapshotPanel
          serverId={snapshotFor.id}
          hostname={snapshotFor.hostname}
          onClose={() => setSnapshotFor(null)}
        />
      )}

      {rollout && (
        <RolloutModal
          rollout={rollout}
          onClose={() => setRollout(null)}
          onOpenPanel={() => {
            setStacksFor({
              id: rollout.serverId,
              hostname: rollout.hostname,
              hasSshKey: rollout.hasSshKey,
            });
            setRollout(null);
          }}
          onCheckHost={() => {
            setCheckFor({ id: rollout.serverId, hostname: rollout.hostname });
            setRollout(null);
          }}
        />
      )}

      {/* Add Server Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface z-10">
              <h2 className="text-base font-semibold">
                {editingId ? 'Server bearbeiten' : 'Server manuell hinzufügen'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Ownership first — it changes the defaults for slots, cost and
                    provider below, so choosing it afterwards would overwrite
                    values the operator had already typed. */}
                <OwnershipSection form={form} setForm={setForm} />

                {/* Basis */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.hostnameRequired')}</label>
                  <div className="flex items-center gap-2">
                    <input
                      required
                      value={form.hostname}
                      onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
                      placeholder={editingId ? 'server-01.example.com' : 'z. B. hoth'}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                    {/* Only when creating: renaming a server should be a
                        deliberate act, not something a suggestion button invites
                        you to do with one stray click. */}
                    {!editingId && (
                      <NameSuggestionButton
                        onPick={(name) => setForm((f) => ({ ...f, hostname: name }))}
                      />
                    )}
                  </div>
                  {editingId && form.hostname !== originalHostname && (
                    <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800">
                      Umbenennen ändert nur den Namen im PC. Bereits ausgerollte Container
                      behalten ihre Namen — abgeleitete Werte wie {'${STACK_PREFIX}'} sind je
                      Host gespeichert und werden mit Absicht nicht neu berechnet, sonst
                      würde der nächste Deploy alle Container neu erstellen. DNS, Monitoring
                      und die Bezeichnung beim Anbieter zieht der PC ebenfalls nicht nach.
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.ipRequired')}</label>
                  <input
                    required
                    value={form.ipAddress}
                    onChange={(e) => setForm((f) => ({ ...f, ipAddress: e.target.value }))}
                    placeholder="1.2.3.4"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Provider</label>
                  <select
                    value={form.provider}
                    onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="manual">Manuell</option>
                    <option value="hetzner">Hetzner</option>
                    <option value="hostinger">Hostinger</option>
                    <option value="aws">AWS</option>
                    <option value="do">DigitalOcean</option>
                    <option value="other">Sonstiger</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.initialStatus')}</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="READY">{t('forms.statusReady')}</option>
                    <option value="ACTIVE">{t('forms.statusActive')}</option>
                    <option value="PROVISIONING">{t('forms.statusProvisioning')}</option>
                    <option value="MAINTENANCE">{t('forms.statusMaintenance')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Region</label>
                  <input
                    value={form.region}
                    onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                    placeholder={t('forms.exampleRegion')}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.maxSlots')}</label>
                  <input
                    type="number"
                    value={form.maxSlots}
                    onChange={(e) => setForm((f) => ({ ...f, maxSlots: e.target.value }))}
                    placeholder="6"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>

                {/* Hardware */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CPU-Kerne</label>
                  <input
                    type="number"
                    value={form.cpuCores}
                    onChange={(e) => setForm((f) => ({ ...f, cpuCores: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">RAM (MB)</label>
                  <input
                    type="number"
                    value={form.ramMb}
                    onChange={(e) => setForm((f) => ({ ...f, ramMb: e.target.value }))}
                    placeholder="8192"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.diskGbPlain')}</label>
                  <input
                    type="number"
                    value={form.diskGb}
                    onChange={(e) => setForm((f) => ({ ...f, diskGb: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>

                {/* GPU — optional, die meisten Hosts haben keine. „Docker-Host"
                    liest Modell und VRAM live vom Server; hier steht, was in der
                    Liste und in der Suche auftauchen soll. */}
                <div className="col-span-2 grid grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      GPU-Modell <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    <input
                      value={form.gpuModel}
                      onChange={(e) => setForm((f) => ({ ...f, gpuModel: e.target.value }))}
                      placeholder={t('forms.exampleGpu')}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      VRAM (MB)
                    </label>
                    <input
                      type="number"
                      value={form.gpuVramMb}
                      onChange={(e) => setForm((f) => ({ ...f, gpuVramMb: e.target.value }))}
                      placeholder="24000"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Anzahl</label>
                    <input
                      type="number"
                      value={form.gpuCount}
                      onChange={(e) => setForm((f) => ({ ...f, gpuCount: e.target.value }))}
                      placeholder="1"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('forms.costPerMonth')}</label>
                  <input
                    value={form.costPerMonth}
                    onChange={(e) => setForm((f) => ({ ...f, costPerMonth: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                {/* Die Farbe steht hier, neben den Notizen: beides ist frei
                    gewählt, trägt keine Logik und hilft nur beim Wiederfinden.
                    Sie in den technischen Feldern zu verstecken würde sie so
                    aussehen lassen, als hinge etwas davon ab. */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Farbe <span className="font-normal text-slate-400">— für die Übersicht</span>
                  </label>
                  <FarbWaehler
                    wert={form.farbe}
                    onWahl={(farbe) => setForm((f) => ({ ...f, farbe: farbe ?? '' }))}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notizen</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  />
                </div>

                {/* SSH — only when creating. While editing, the key is changed
                    through the dedicated "Ersetzen" dialog, which validates and
                    versions it; offering an empty field here would read as
                    "clear the key". */}
                {/* Benutzer und Port sind Verbindungsdaten, kein Schlüsselmaterial.
                    Sie standen bisher innerhalb von SshKeySection und waren damit
                    beim Bearbeiten unsichtbar — ein falsch eingetragener Benutzer
                    ließ sich nicht mehr korrigieren, obwohl das Backend es die
                    ganze Zeit erlaubt hat. */}
                <div className="col-span-2 grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      SSH-Benutzer
                    </label>
                    <input
                      value={form.sshUser}
                      onChange={(e) => setForm((f) => ({ ...f, sshUser: e.target.value }))}
                      placeholder="root"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      Bei AWS meist <code>ubuntu</code> oder <code>ec2-user</code>, bei Hetzner
                      und Hostinger <code>root</code>.
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      SSH-Port
                    </label>
                    <input
                      type="number"
                      value={form.sshPort}
                      onChange={(e) => setForm((f) => ({ ...f, sshPort: e.target.value }))}
                      placeholder="22"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>

                {!editingId ? (
                  <SshKeySection form={form} setForm={setForm} />
                ) : (
                  <p className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                    Der SSH-Schlüssel wird hier nicht geändert — dafür in der Liste auf
                    „Ersetzen" klicken. Benutzer und Port oben sind davon unabhängig und
                    lassen sich jederzeit korrigieren.
                  </p>
                )}

                {/* Base stacks, creation only: rolling out needs a server record
                    to attach the deployment to, and on an existing host the panel
                    per server is the place for it — it shows what is already
                    there. */}
                {!editingId && (
                  <div className="col-span-2 border-t border-slate-200 pt-4">
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                      <Layers size={14} className="text-slate-400" />
                      Base-Stacks nach dem Anlegen ausrollen
                    </label>
                    {form.sshPrivateKey ? (
                      <StackChecklist selected={selectedStacks} setSelected={setSelectedStacks} />
                    ) : (
                      <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-snug text-slate-500">
                        Dafür wird ein SSH-Schlüssel gebraucht — oben einen hinterlegen oder
                        generieren. Ohne Schlüssel lässt sich der Server anlegen und später
                        über „Base-Stacks" in der Liste bestücken.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingId(null);
                  }}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60 flex items-center gap-2"
                >
                  {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Server speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Replace Key Modal */}
      {replaceKeyFor && (
        <ReplaceKeyModal
          server={replaceKeyFor}
          onClose={() => setReplaceKeyFor(null)}
        />
      )}

      {/* Auto-Provision Modal */}
      {showProvisionModal && <ProvisionModal onClose={() => setShowProvisionModal(false)} />}

      {/* Die Wache gehoert hierher und nicht zu einer einzelnen Instanz: sie
          handelt je WIRT — ein Beiwagen-Neustart bringt alle Adressen dieses
          Servers zurueck, nicht nur die, die den Verdacht ausgeloest hat. */}
      <ProxyWachePanel />
    </div>
  );
}
